import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { PermissionService } from 'app/core/auth/permission.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { Observable, ReplaySubject, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private _httpClient = inject(HttpClient);
    private _permissionService = inject(PermissionService);
    private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data, filtered by user permissions
     */
    get(): Observable<Navigation> {
        return this._httpClient.get<Navigation>('api/common/navigation').pipe(
            tap((navigation) => {
                const filtered: Navigation = {
                    compact: this._filterByPermission(navigation.compact),
                    default: this._filterByPermission(navigation.default),
                    futuristic: this._filterByPermission(navigation.futuristic),
                    horizontal: this._filterByPermission(navigation.horizontal),
                };
                this._navigation.next(filtered);
            })
        );
    }

    /**
     * Re-fetch and re-filter navigation (e.g. after impersonation changes permissions).
     */
    rebuild(): void {
        this.get().subscribe();
    }

    /**
     * Recursively filter navigation items based on user permissions.
     * Items without meta.permission are always shown.
     */
    private _filterByPermission(items: FuseNavigationItem[]): FuseNavigationItem[] {
        return items
            .filter((item) => {
                const permission = item.meta?.permission;
                const permissions: string[] = item.meta?.permissions;
                if (permissions?.length) return this._permissionService.hasAnyPermission(...permissions);
                if (!permission) return true;
                return this._permissionService.hasPermission(permission);
            })
            .map((item) => {
                if (item.children?.length) {
                    return { ...item, children: this._filterByPermission(item.children) };
                }
                return item;
            })
            .filter((item) => {
                // Remove collapsable/group items that have no visible children
                if ((item.type === 'collapsable' || item.type === 'group') && item.children?.length === 0) {
                    return false;
                }
                return true;
            });
    }
}
